import type { ReactNode } from "react";
import classNames from "classnames";
import {
    AccountsIcon,
    ChartOfAccountsIcon,
    TransactionsIcon,
    TriangleIcon,
} from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import sideNavStyles from "./ComponentAnatomySchematicSideNav.module.scss";
import styles from "./ComponentAnatomyView.module.scss";

type Read = ReturnType<typeof bindingReader>;

const rowContent: Readonly<
    Record<string, { label: string; icon?: ReactNode }>
> = {
    item: { label: "Accounts", icon: <AccountsIcon /> },
    activeItem: { label: "Transactions", icon: <TransactionsIcon /> },
    subItem: { label: "Categories" },
    section: { label: "Accounting" },
};

/** A resting item row for context around the state row. */
const PlainRow = ({
    label,
    icon,
    trailing,
}: {
    label: string;
    icon: ReactNode;
    trailing?: ReactNode;
}) => (
    <span className={sideNavStyles.plainRow}>
        <span className={sideNavStyles.glyph}>{icon}</span>
        <span className={sideNavStyles.label}>{label}</span>
        {trailing}
    </span>
);

/** The row the variant and state select, painted from the resolved bindings. */
const StateRow = ({
    read,
    highlight,
    variantId,
}: {
    read: Read;
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const { label, icon } = rowContent[variantId] ?? rowContent.item;
    const ringWidth = read("focusRing", "box-shadow (width)");
    const ringColor = read("focusRing", "box-shadow (color)");
    const isSection = variantId === "section";

    return (
        <Part
            partId="itemBackground"
            highlight={highlight}
            className={sideNavStyles.row}
            style={{
                height: read("itemBackground", "height"),
                paddingInlineStart:
                    read("itemBackground", "padding-inline-start") ??
                    read("itemBackground", "padding-inline"),
                paddingInlineEnd:
                    read("itemBackground", "padding-inline-end") ??
                    read("itemBackground", "padding-inline"),
                marginInlineStart: read(
                    "itemBackground",
                    "margin-inline-start",
                ),
                gap:
                    read("itemBackground", "gap (icon to label)") ??
                    read("itemBackground", "gap (label to caret)"),
                borderRadius: read("itemBackground", "border-radius"),
                backgroundColor: read("itemBackground", "background-color"),
            }}
        >
            {icon !== undefined && (
                <Part
                    partId="glyph"
                    highlight={highlight}
                    className={sideNavStyles.glyph}
                    style={{
                        width: read("glyph", "width"),
                        height: read("glyph", "height"),
                        color: read("glyph", "color"),
                    }}
                >
                    {icon}
                </Part>
            )}
            <Part
                partId="itemLabel"
                highlight={highlight}
                className={sideNavStyles.label}
                style={{
                    color: read("itemLabel", "color"),
                    fontFamily: read("itemLabel", "font-family"),
                    fontSize: read("itemLabel", "font-size"),
                    fontWeight: read("itemLabel", "font-weight"),
                    lineHeight: read("itemLabel", "line-height"),
                    letterSpacing: read("itemLabel", "letter-spacing"),
                }}
            >
                {label}
            </Part>
            {isSection && (
                <Part
                    partId="glyph"
                    highlight={highlight}
                    className={classNames(
                        sideNavStyles.glyph,
                        sideNavStyles.caret,
                    )}
                    style={{
                        width: read("glyph", "width"),
                        height: read("glyph", "height"),
                        color: read("glyph", "color"),
                        borderRadius: read("glyph", "border-radius"),
                    }}
                >
                    <TriangleIcon />
                </Part>
            )}
            {ringWidth !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={sideNavStyles.ring}
                    style={{
                        borderRadius: read("itemBackground", "border-radius"),
                        boxShadow: `inset 0 0 0 ${ringWidth} ${ringColor}`,
                    }}
                />
            )}
        </Part>
    );
};

/**
 * X-ray plate for SideNav: a strip of nav surface, as wide as the plate allows
 * up to the bound width, holding the row kind the variant names in context.
 */
export const SideNavSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);
    const stateRow = (
        <StateRow read={read} highlight={highlight} variantId={variantId} />
    );
    const accountsRow = <PlainRow label="Accounts" icon={<AccountsIcon />} />;
    const transactionsRow = (
        <PlainRow label="Transactions" icon={<TransactionsIcon />} />
    );
    const rowsByVariant: Readonly<Record<string, ReactNode>> = {
        item: (
            <>
                {stateRow}
                {transactionsRow}
            </>
        ),
        activeItem: (
            <>
                {accountsRow}
                {stateRow}
            </>
        ),
        subItem: (
            <>
                <PlainRow
                    label="Transactions"
                    icon={<TransactionsIcon />}
                    trailing={
                        <span
                            className={classNames(
                                sideNavStyles.glyph,
                                sideNavStyles.caret,
                            )}
                        >
                            <TriangleIcon />
                        </span>
                    }
                />
                <span className={sideNavStyles.subItems}>
                    <Part
                        partId="connector"
                        highlight={highlight}
                        className={sideNavStyles.connector}
                        style={{
                            left: read("connector", "left"),
                            width: read("connector", "width"),
                            backgroundColor: read(
                                "connector",
                                "background-color",
                            ),
                        }}
                    />
                    {stateRow}
                    <span className={sideNavStyles.plainSubRow}>Rules</span>
                </span>
            </>
        ),
        section: (
            <>
                {stateRow}
                <PlainRow
                    label="Chart of Accounts"
                    icon={<ChartOfAccountsIcon />}
                />
            </>
        ),
    };

    return (
        <span className={classNames(styles.schematic, sideNavStyles.schematic)}>
            <Part
                partId="surface"
                highlight={highlight}
                className={sideNavStyles.surface}
                style={{
                    maxWidth: read("surface", "width"),
                    backgroundColor: read("surface", "background-color"),
                    borderRightColor: read("surface", "border-right-color"),
                    borderRightWidth: read("surface", "border-right-width"),
                    paddingInline: read("surface", "padding-inline"),
                }}
            >
                {rowsByVariant[variantId] ?? rowsByVariant.item}
            </Part>
        </span>
    );
};
